/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import styled from "styled-components";
import Label from "../Label";
import Icon from "../Icon";
import { useController } from "react-hook-form";
import { StyledFormField } from "./StyledFormField";
import { useTranslate } from "../../../hooks/useTranslate";
import { useToggleAriaProps } from "../../../hooks/a11y/useToggleAriaProps";

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const EyeIconWrapper = styled.button`
  display: flex;
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
`;

const PasswordInput = ({
  control,
  name,
  label,
  required = false,
  error = false,
  defaultValue,
  validationRules = {},
  placeholder,
  ...props
}) => {
  const { _t } = useTranslate();
  const [showPassword, setShowPassword] = useState(false);
  const { field, fieldState } = useController({
    name,
    control,
    rules: {
      required: required ? "error_value_is_empty" : undefined,
      ...validationRules,
    },
    defaultValue: defaultValue || "",
  });

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleVisibilityAria = useToggleAriaProps(showPassword, "Hide password", "Show password");

  return (
    <>
      {label ? (
        <Label name={name} required={required}>
          {label}
        </Label>
      ) : undefined}
      <PasswordInputWrapper>
        <StyledFormField.Input
          {...field}
          id={name}
          type={showPassword ? "text" : "password"}
          $error={fieldState.error}
          aria-invalid={!!fieldState.error}
          aria-required={required || undefined}
          aria-describedby={fieldState.error ? `${name}-error` : undefined}
          placeholder={_t(placeholder)}
          {...props}
        />
        <EyeIconWrapper
          type="button"
          onClick={togglePasswordVisibility}
          {...toggleVisibilityAria}
        >
          {showPassword ? <Icon.EyeCrossed /> : <Icon.Eye />}
        </EyeIconWrapper>
      </PasswordInputWrapper>
    </>
  );
};

export default PasswordInput;
